import { useEffect, useState, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Issue #543 — captura PRECOCE do beforeinstallprompt no nível do módulo.
//
// O evento dispara UMA única vez por carregamento, tipicamente logo após o
// service worker ficar ativo — ou seja, ANTES do React montar o botão (o App
// só renderiza InstallFloatButton depois de authReady/fetchMe). Um listener
// registrado dentro de useEffect perdia o evento definitivamente:
// deferredPrompt ficava null -> canInstall=false -> o clique navegava para a
// página "Como instalar" mesmo com o prompt nativo disponível.
//
// O listener abaixo é registrado na avaliação do módulo (antes de qualquer
// render) e guarda o evento em `deferredPrompt`. Instâncias do hook montadas
// depois sincronizam via `subscribers`.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();
const notifySubscribers = () => subscribers.forEach((fn) => fn());

// Flag persistida de instalação: `appinstalled` só dispara na sessão em que o
// usuário instala; sem persistência, o botão reaparecia em visitas seguintes
// pelo browser com o app já instalado (display-mode standalone só vale dentro
// da janela do app instalado). Se o usuário desinstalar, o próximo
// beforeinstallprompt limpa a flag — o evento só dispara quando o app NÃO está
// instalado — então o estado se autocorrige.
const INSTALLED_KEY = 'auranota_pwa_installed';

function persistInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
  } catch {
    // localStorage indisponível (modo privado etc.) — apenas não persiste.
  }
}

function markInstalled() {
  persistInstalled();
  deferredPrompt = null;
  notifySubscribers();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    // BIP só dispara quando o app NÃO está instalado: limpa flag stale de uma
    // eventual desinstalação.
    try {
      localStorage.removeItem(INSTALLED_KEY);
    } catch {
      // ignore
    }
    deferredPrompt = e as BeforeInstallPromptEvent;
    notifySubscribers();
  });
  window.addEventListener('appinstalled', markInstalled);
}

function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  if (standalone) return true;
  try {
    return localStorage.getItem(INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Hook global que captura beforeinstallprompt no nível do App.
 * O evento beforeinstallprompt dispara uma única vez (geralmente na home page).
 * Este hook captura o evento assim que dispara, independentemente da página atual.
 * O botão de instalação pode então ser mostrado em qualquer lugar (header, etc).
 */
export function usePWAInstall() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(deferredPrompt);
  // Lazy init evita flash de 1 frame do botão quando o app já está instalado.
  const [isInstalled, setIsInstalled] = useState<boolean>(detectInstalled);
  const [installOutcome, setInstallOutcome] = useState<'accepted' | 'dismissed' | null>(null);

  useEffect(() => {
    // Sincroniza com o estado global (evento pode ter disparado antes do mount,
    // ou entre o primeiro render e este efeito).
    const sync = () => {
      setPrompt(deferredPrompt);
      setIsInstalled(detectInstalled());
    };
    subscribers.add(sync);
    sync();

    // Escutar mudanças de display-mode (ex.: janela standalone aberta/fechada)
    const mql = window.matchMedia('(display-mode: standalone)');
    const onDisplayChange = (e: MediaQueryListEvent) => setIsInstalled(e.matches || detectInstalled());
    mql.addEventListener('change', onDisplayChange);

    return () => {
      subscribers.delete(sync);
      mql.removeEventListener('change', onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    const event = deferredPrompt;
    // O evento é de uso único: limpa o global para todas as instâncias do hook
    // antes de promptar (evita segundo .prompt() num evento já consumido).
    deferredPrompt = null;
    notifySubscribers();
    await event.prompt();
    const choice = await event.userChoice;
    setInstallOutcome(choice.outcome);
    if (choice.outcome === 'accepted') {
      // appinstalled também marca; redundância segura.
      markInstalled();
      return true;
    }
    return false;
  }, []);

  const canInstall = !!prompt && !isInstalled;

  return { canInstall, isInstalled, installOutcome, promptInstall };
}
